import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { Divider, InputNumber } from 'antd';
import React, { useEffect, useState } from 'react';
import ModalComponent from '../../../../../components/ModalComponent';

const ConfigRankingModal = ({ isModalOpen, setIsModalOpen, items, onFinish, loadings }) => {
  const [editedValues, setEditedValues] = useState({});

  useEffect(() => {
    if (!isModalOpen) return;
    const initial = {};
    items.forEach((item) => {
      initial[item.id] = parseFloat(item.value) || 0;
    });
    setEditedValues(initial);
  }, [isModalOpen, items]);

  const total = Object.values(editedValues).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const roundedTotal = Math.round(total * 10) / 10;
  const isValid = Math.abs(roundedTotal - 100) < 0.001;
  const remaining = Math.round((100 - roundedTotal) * 10) / 10;
  const hasZero = Object.values(editedValues).some((v) => v === 0);

  const handleSave = () => {
    const changes = items.map((item) => ({
      ...item,
      newValue: String(editedValues[item.id] ?? parseFloat(item.value) ?? 0),
    }));
    onFinish(changes);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const totalColor = (() => {
    if (hasZero) return '#ff4d4f';
    if (isValid) return '#52c41a';
    if (roundedTotal > 100) return '#ff4d4f';
    return '#faad14';
  })();

  const totalStatusIcon = (() => {
    if (hasZero) {
      return <ExclamationCircleFilled style={{ color: '#ff4d4f', marginRight: 6 }} />;
    }
    if (isValid) {
      return <CheckCircleFilled style={{ color: '#52c41a', marginRight: 6 }} />;
    }
    return <ExclamationCircleFilled style={{ color: totalColor, marginRight: 6 }} />;
  })();

  const totalStatusText = (() => {
    if (hasZero) return 'Each rank must be greater than 0%';
    if (isValid) return 'Distribution is valid';
    if (roundedTotal > 100) return `Exceeds 100% by ${Math.abs(remaining)}%`;
    return `${remaining}% remaining to allocate`;
  })();

  return (
    <ModalComponent
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={handleSave}
      title="Edit Ranking Distribution"
      okText="Save"
      confirmLoading={loadings}
      okButtonProps={{ disabled: !isValid || hasZero || loadings }}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      <Divider />

      <div
        style={{
          background: '#fafafa',
          border: `1px solid ${totalColor}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: '#555' }}>
          {totalStatusIcon}
          <span>{totalStatusText}</span>
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: totalColor,
            lineHeight: 1,
          }}
        >
          {roundedTotal}%
          <span style={{ fontSize: 13, fontWeight: 400, color: '#888', marginLeft: 4 }}>
            / 100%
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: 6,
            }}
          >
            <span
              style={{
                flex: 1,
                fontWeight: 500,
                fontSize: 14,
                color: '#222',
                marginRight: 16,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.itemKey || '-'}
            </span>
            <InputNumber
              value={editedValues[item.id] ?? 0}
              onChange={(val) =>
                setEditedValues((prev) => ({ ...prev, [item.id]: val ?? 0 }))
              }
              onKeyDown={(e) => {
                const allowed = [
                  'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight',
                  'ArrowUp', 'ArrowDown', 'Home', 'End',
                ];
                const isDigit = e.key >= '0' && e.key <= '9';
                const isDot = e.key === '.';
                if (!isDigit && !isDot && !allowed.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              min={0}
              max={100}
              precision={1}
              step={0.1}
              addonAfter="%"
              style={{ width: 130 }}
            />
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div style={{ textAlign: 'center', color: '#999', padding: '24px 0' }}>
          No ranking items found.
        </div>
      )}
    </ModalComponent>
  );
};

export default ConfigRankingModal;
